import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CabinetCdkDialogService } from '../dialog/cabinet-cdk-dialog.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UserPromocodeService } from '@nebalkon-workspace/auth';
import type { Promocode } from '@nebalkon-workspace/common';
import {
  CalculatorPricingConfigService,
  buildVolumeDiscountTierViews,
  getMaxMarketingCombinedDiscountPercent,
  getMinStorageBoxDailyBynd,
} from '@nebalkon-workspace/cabinet-core';
import { NotSystemNotificationService } from '@nebalkon-workspace/ui-kit';
import { CabinetButtonComponent } from '../ui/cabinet-button.component';
import { CabinetServiceStatusHintComponent } from '../help/cabinet-service-status-hint.component';
import {
  StoragePickupPromocodeModalComponent,
  type StoragePickupPromocodeModalData,
} from '../storage-pickup/storage-pickup-promocode-modal.component';

const STORAGE_BOX_VOLUME_M3 = 0.1;

const BENEFIT_ITEMS = [
  { key: 'DAILY', iconSrc: '/icons/core/calendar-check.svg' },
  { key: 'VOLUME', iconSrc: '/icons/core/warehouse.svg' },
  { key: 'LOGISTICS', iconSrc: '/icons/login/login-benefit-delivery.svg' },
] as const;

function asWalletPromocode(p: Promocode): Promocode {
  const discount = Number(p.discount);
  return {
    promocode: p.promocode.trim().toUpperCase(),
    discount: Number.isFinite(discount) ? discount : 0,
  };
}

@Component({
  selector: 'lib-cabinet-tariffs-view',
  imports: [TranslatePipe, CabinetButtonComponent, CabinetServiceStatusHintComponent],
  templateUrl: './cabinet-tariffs-view.component.html',
  styleUrl: './cabinet-tariffs-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CabinetTariffsViewComponent {
  readonly #dialog = inject(CabinetCdkDialogService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #translate = inject(TranslateService);
  readonly #userPromocode = inject(UserPromocodeService);
  readonly #notification = inject(NotSystemNotificationService);
  readonly #pricingConfig = inject(CalculatorPricingConfigService);

  protected readonly benefitItems = BENEFIT_ITEMS;
  protected readonly boxImageSrc = '/images/sections/storage-standard-box-nebalkon.webp';

  protected readonly promocode = signal<Promocode | null>(null);

  protected readonly pricingLoading = computed(
    () =>
      this.#pricingConfig.config() === null && !this.#pricingConfig.loadError(),
  );

  protected readonly pricingLoadError = computed(() =>
    this.#pricingConfig.loadError(),
  );

  protected readonly rates = computed(() => {
    const storage = this.#pricingConfig.config()?.storage;
    if (!storage) {
      return null;
    }

    return {
      minDailyBynd: getMinStorageBoxDailyBynd(STORAGE_BOX_VOLUME_M3),
      baseRateByndPerM3Day: storage.baseRateByndPerM3Day,
      maxStandardDiscountPercent: getMaxMarketingCombinedDiscountPercent(),
      minVolumeM3: storage.minVolumeM3,
    };
  });

  protected readonly volumeTiers = computed(() => {
    const storage = this.#pricingConfig.config()?.storage;
    if (!storage) {
      return [];
    }

    return buildVolumeDiscountTierViews(
      storage.minVolumeM3,
      storage.volumeDiscountTiers,
    );
  });

  protected readonly serviceRates = computed(
    () => this.#pricingConfig.config()?.storage.serviceRates ?? null,
  );

  protected readonly maxPromoPercent = computed(() => {
    const maxPromoDiscountPercent =
      this.#pricingConfig.config()?.storage.maxPromoDiscountPercent;
    if (maxPromoDiscountPercent == null) {
      return null;
    }
    return Math.round(maxPromoDiscountPercent * 100);
  });

  protected readonly promocodeCtaKey = computed(() =>
    this.promocode()
      ? 'CLIENT.CABINET.WALLET.PROMOCODE.CHANGE'
      : 'CLIENT.CABINET.WALLET.PROMOCODE.APPLY',
  );

  constructor() {
    effect(() => {
      const applied = this.#userPromocode.appliedPromocode();
      untracked(() => {
        this.promocode.set(applied ? asWalletPromocode(applied) : null);
      });
    });
  }

  protected retryLoadPricing(): void {
    void this.#pricingConfig.load();
  }

  protected openPromocodeModal(): void {
    const dialogRef = this.#dialog.open<Promocode | null | undefined>(
      StoragePickupPromocodeModalComponent,
      {
        data: {
          promocode: this.promocode(),
        } satisfies StoragePickupPromocodeModalData,
        panelClass: 'cabinet-dialog-panel',
      },
    );

    dialogRef.closed
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((result) => {
        if (result === undefined) {
          return;
        }

        if (result) {
          const promocode = asWalletPromocode(result);
          this.promocode.set(promocode);
          this.#notification.addToQueue({
            id: `tariffs-promocode-save-${Date.now()}`,
            type: 'success',
            text: [
              this.#translate.instant(
                'CLIENT.CABINET.WALLET.PROMOCODE.SAVE_SUCCESS',
              ),
            ],
          });
          return;
        }

        this.promocode.set(null);
      });
  }

  protected formatPrice(value: number, fractionDigits = 2): string {
    const rounded =
      Math.round(value * 10 ** fractionDigits) / 10 ** fractionDigits;
    let fractional = rounded.toFixed(fractionDigits);
    fractional = fractional.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
    const [integerPart, fractionalPart = ''] = fractional.split('.');
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    if (!fractionalPart) {
      return formattedInteger;
    }
    return `${formattedInteger},${fractionalPart}`;
  }

  protected formatPercent(value: number): string {
    const percent = Math.round(value * 1000) / 10;
    return `${percent}%`;
  }

  protected formatVolumeM3(value: number): string {
    return value.toFixed(1).replace('.', ',');
  }
}
